<template>
  <ws-wrapper
    :wrap="!!tooltip"
    element="span"
    class="ws-button-wrapper"
  >
    <q-btn
      ref="qBtn"
      unelevated
      v-bind="$attrs"
      :loading="ajax ? ajaxLoading : loading"
      class="ws-button"
      :flat="flat"
      :class="[
        ...(buttonClasses ? buttonClasses : []),
        {
          primary,
          secondary,
          ghost,
          light,
          small,
          smaller,
          dark,
          'darken-on-hover': darkenOnHover,
          'is-loading': loading || ajaxLoading,
          'use-paddings': usePaddings,
          'icon-only': iconOnly,
          'icon-right': iconRight,
        }
      ]"
      :ripple="isTheme(Theme.ADMIN)"
      v-on="!ajax ? $listeners : { click: buttonAjaxCall }"
    >
      <!-- credit: https://stackoverflow.com/a/50892881/861615 -->
      <template v-for="(_, slot) of slots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope"></slot>
      </template>

      <ws-icon
        v-if="icon && !iconRight"
        class="ws-button-icon"
        :color="iconColor"
        :class="`ws-button-icon--${icon}`"
        :name="icon"
        :size="iconSizeCalc"
      />

      <!-- BEWARE $slots.default is not reactive, cannot be moved to computed values -->
      <div
        v-if="$slots.default || label"
        class="slot-content"
        :class="{
          'q-pl-sm': iconAndContent && !iconRight,
          'q-pr-sm': iconAndContent && iconRight,
          'ws-typo-button-desktop': forcedDesktop,
        }"
      >
        <slot></slot>
        <span
          v-if="label"
          :class="{ 'ws-typo-button-desktop': forcedDesktop }"
        >{{ $t(label) }}</span>
      </div>

      <ws-icon
        v-if="icon && iconRight"
        class="ws-button-icon"
        :class="`ws-button-icon--${icon}`"
        :name="icon"
        :size="iconSizeCalc"
      />

      <template
        v-if="useThemeSpinner"
        v-slot:loading
      >
        <ws-spinner :color="loaderColorResolved" :width="isTheme(Theme.ACTIVE_24) ? '50px' : undefined" />
      </template>
    </q-btn>
    <ws-tooltip
      v-if="tooltip"
      :content-class="tooltipClass"
      :anchor="tooltipAnchor"
      :self="tooltipSelf"
      :delay="200"
      arrow-position="bottom"
    >
      {{ $t(tooltip) }}
    </ws-tooltip>
  </ws-wrapper>
</template>

<script lang="ts">
import {Component, Vue, Prop} from 'vue-property-decorator';
import {QBtn} from 'quasar';
import {config, Theme} from '@loopia-group/services';
import WsIcon from './WsIcon.vue';
import axios, {type Method} from 'axios';
import {responseAction} from '../services/reponseAction.service';
import type { ActionOptions } from '../services/responseAction.service';
import WsTooltip from './WsTooltip.vue';
import WsSpinner from './WsSpinner.vue';

@Component({
  components: {
    WsSpinner,
    QBtn,
    WsIcon,
    WsTooltip
  },
})
export class WsButton extends Vue {
  @Prop(String) readonly label!: string;
  //  Styles
  @Prop(Array) readonly buttonClasses!: string[];
  @Prop(Boolean) readonly primary!: boolean;
  @Prop(Boolean) readonly secondary!: boolean;
  @Prop(Boolean) readonly ghost!: boolean;
  @Prop(Boolean) readonly light!: boolean;
  @Prop(Boolean) readonly flat!: boolean;
  @Prop(Boolean) readonly small!: boolean;
  @Prop(Boolean) readonly smaller!: boolean;
  @Prop(Boolean) readonly forcedDesktop!: boolean; // forced desktop font-size/line-height for every screen size
  @Prop(Boolean) readonly dark!: boolean; // dark mode
  @Prop(Boolean) readonly usePaddings!: boolean; // force to use paddings for button text if needed
  @Prop() readonly loaderColor!: string;
  @Prop({default: null}) readonly icon!: string;
  @Prop(String) readonly iconColor?: string;
  @Prop(Boolean) readonly iconRight!: boolean; // icon on right side oflabel
  @Prop() readonly iconSize!: number;
  @Prop() readonly darkenOnHover!: boolean;
  @Prop(Boolean) readonly loading!: boolean;

  readonly desktop!: boolean;
  readonly tablet!: boolean;
  readonly mobile!: boolean;

  //  Tooltip
  @Prop(String) readonly tooltip!: string;
  @Prop({type: String, default: 'top middle'}) readonly tooltipAnchor!: string;
  @Prop({type: String, default: 'bottom middle'}) readonly tooltipSelf!: string;
  @Prop({type: String, default: 'ws-tooltip ws-arrow-bottom'}) readonly tooltipClass!: string;

  // Ajax
  @Prop({type: Boolean, default: false}) readonly ajax!: boolean;
  @Prop(String) readonly ajaxMethod!: Method;
  @Prop(String) readonly url!: string;
  @Prop([Object, Array]) readonly ajaxParams!: any;
  @Prop(Object) readonly onSuccess!: ActionOptions;
  @Prop({default: null}) readonly ajaxErrorMessagePath!: string|null;
  config = config;
  Theme = Theme;
  ajaxLoading = false;

  async buttonAjaxCall() {
    try {
      this.$emit('ajax-action-start');
      this.ajaxLoading = true;

      const {data, headers} = await axios({
        url: this.url,
        data: this.ajaxParams,
        method: this.ajaxMethod,
        headers: this.requestHeaders
      });

      const actionResponse = await responseAction(data, headers, this.onSuccess, this);
      this.$emit('ajax-action-success', data);
      this.ajaxLoading = await actionResponse.actionPromise as boolean;
    } catch (error) {
      if (this.ajaxErrorMessagePath !== null) {
        this.$root.$messageService.errorHandler(this.ajaxErrorMessagePath)(error);
      } else {
        this.$root.$messageService.errorHandler()(error);
      }

      this.$emit('ajax-action-failed', error);
      this.ajaxLoading = false;
    }
  }

  get requestHeaders() {
    const headers: any = {};

    // @ts-ignore
    if (!this.onSuccess?.reload && !this.onSuccess?.redirect) {
      headers['X-WITH-FLASH-MESSAGES'] = '1';
    }

    return headers;
  }

  get loaderColorResolved() {
    if (this.loaderColor) {
      return this.loaderColor;
    } else if (this.primary) {
      return 'dark';
    } else if (this.secondary) {
      return 'white';
    }
    return undefined; // default
  }

  get useThemeSpinner() {
    return config.themeOptions?.spinner;
  }

  get slots() {
    const slots = {...this.$scopedSlots};
    // all but default, because default is handled differently
    delete slots.default;
    if (config.themeOptions?.spinner) {
      delete slots.loading;
    }
    return slots;
  }

  // I don't see how breaking this into multiple functions would help
  // eslint-disable-next-line sonarjs/cognitive-complexity
  get iconSizeCalc() {
    // CAUTION! icon height affects button height!
    let defaultSize = config.themeOptions?.iconSize;

    if (!defaultSize && config.themeOptions?.btnDynamicIconSize) {
      defaultSize =
        (this.forcedDesktop && 24) ||
        (this.desktop && 24) ||
        (this.tablet && 20) ||
        (this.mobile && 16.8);
    } else {
      defaultSize = defaultSize || 22;
    }

    return this.iconSize || (this.flat && this.small ? 14 : defaultSize);
  }

  get iconOnly() {
    return this.icon && !this.$slots.default && !this.label;
  }

  get iconAndContent() {
    return this.icon && (this.$slots.default || this.label);
  }
}

export default WsButton;
</script>

<style lang="scss" scoped>
span.ws-button-wrapper {
  display: inline-block;
}

button.q-btn.ws-button,
a.q-btn.ws-button {
  border-radius: $ws-border-radius;

  ::v-deep {
    .q-btn__content {
      flex-wrap: nowrap;
    }
  }

  &.is-loading {
    cursor: not-allowed;
  }

  .ws-cary-spinner {
    max-width: 70%;
    max-height: 70%;
  }

  &:not([class*=' text-']) {
    // default color
    color: $button-primary-text;
  }

  &.primary,
  &.secondary {
    border: 0;
  }

  &.light:before {
    background-color: $ws-white;
  }

  &.primary {
    &:before {
      background-color: $button-primary-bg;
    }

    &.darken-on-hover {
      background-color: $button-primary-bg-hover;
      transition: background-color 150ms;
      &:hover {
        background-color: #0423D0 !important;
      }
    }
  }

  &.secondary:before {
    background-color: $ws-secondary;
  }

  &.ghost {
    &:before {
      background-color: transparent;
    }

    // z-index: 2;
    &.dark {
      color: $ws-white;

      &:after {
        border-color: $ws-white;
      }
    }

    &:after {
      // z-index: 1;
      background-color: transparent;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      border: 1px solid $ws-base130;
    }
  }

  &.use-paddings {
    padding-left: map-get($space-xs, x) !important;
    padding-right: map-get($space-xs, x) !important;
  }

  .slot-content {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .dashboard-block__header--with-search & {
    min-height: 40px;
    padding: map-get($space-xs, y) map-get($space-sm, x) map-get($space-xs, y) map-get($space-md, x);
  }
}
</style>
